import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CompanyId } from '@business/entities/companies';
import { ClientResponse, ClientTemplateResponse } from '@business/entities/client';
import { ClientAPI } from '@business/api/client_api';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class ClientBloc extends BaseBloc {
  private readonly clientsRepo = new Repository<ClientResponse>({
    getItemId: client => client.id,
  });

  readonly clients$ = this.clientsRepo.items$;
  readonly selectClient = this.clientsRepo.selectItem;

  onReset() {
    this.clientsRepo.reset();
  }

  fetchClients = (companyId: CompanyId) => {
    return ClientAPI.fetchClients(companyId).pipe(this.clientsRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_clients_error') }));
  };

  createClient = (client: Partial<ClientResponse>) => {
    return ClientAPI.createClient(client).pipe(
      this.clientsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_client'),
      }),
    );
  };

  updateClientTemplate = (data: ClientTemplateResponse) => {
    return showDeleteConfirmation(
      'Confirm Push Template',
      'THIS WILL OVERWRITE YOUR CLIENT ACCOUNT. ONLY DO THIS IF YOU ARE ABSOLUTELY CERTAIN THAT YOU WANT TO CHANGE THIS.',
      { btnLabel: 'Push Template' },
    ).pipe(
      switchMap(() => ClientAPI.updateClientTemplate(data)),
      handleMessage({
        type: requestMessage('create_client'),
      }),
    );
  };

  updateClientGraphTemplate = (data: ClientTemplateResponse) => {
    return showDeleteConfirmation('Confirm Push Template', 'There is already a Stat Group in the client account. Do you want to continue anyway?', {
      btnLabel: 'Push Template',
    }).pipe(
      switchMap(() => ClientAPI.updateClientTemplate(data)),
      handleMessage({
        type: requestMessage('create_client'),
      }),
    );
  };

  deleteClient = (id: CompanyId) => {
    return showDeleteConfirmation('Delete Account', 'Do you really want to cancel this account?', { btnLabel: 'Yes, Delete' }).pipe(
      switchMap(() => ClientAPI.deleteClient(id)),
      this.clientsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_client'),
      }),
    );
  };
}
